import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { categoryIcon } from "constants/categoryIcons";
import useDismiss from "hooks/useDismiss";
import useHoverIntent from "hooks/useHoverIntent";
import { buildClassificationPath } from "utils/productRoutes";

import { NAV_ITEM_CLASS, NAV_MENU_GROUP } from "./navigationItems";

import { Icon } from "@/design-system";

/**
 * The navbar's "All categories" mega-menu, ported from the reference's updated nav
 * (grep `data-catmenu` in
 * `resources/Dental supply storefront design system/Dens N Dente Tailwind.dc.html`).
 *
 * It replaced a 480px two-column grid of 8 icon tiles. The reference now uses a
 * **two-pane** panel: a 248px scrolling list of every category on the left, and the
 * hovered category's subcategories on the right, above a "Browse all <category>" link.
 *
 * Things about it that aren't obvious:
 *
 * - **It opens on click, not hover.** The trigger toggles it; an outside click, Escape or
 *   choosing a link closes it. `useHoverIntent` is still used for its open/close state and
 *   its `NAV_MENU_GROUP` mutual exclusion — its pointer handlers are not bound.
 * - **The open animation is a morph, and it lives in CSS.** `.cat-morph` grows a 48x40
 *   pill into 820px x min(540px, 100vh-180px) while `.cat-morph-menu` counter-animates
 *   opacity / translate / blur, with different durations per direction. Both are in
 *   `design-system/components/keyframes.js` along with the `--morph-*` variables; the
 *   pairing can't be expressed as utilities. The panel therefore stays mounted and is
 *   driven by `data-open`, rather than being conditionally rendered.
 * - **The right pane follows hover, not click.** Each left-hand row sets the active
 *   category on `mouseenter` *and* `focus`, so keyboard tabbing through the list updates
 *   the pane too. Clicking a row navigates to that category.
 * - **Icons come from the reference's own table**, keyed by classification id — see
 *   `constants/categoryIcons.js` (shared with the `/categories` page). They are not
 *   derived from the category name or its position.
 * - **The active row's styling comes from the reference's own computed classes**:
 *   `bg-soft text-blue-700` with a `text-blue-600` icon when active, otherwise
 *   `text-ink-2` / `text-ink-3` with a `hover:bg-soft hover:text-blue-700` pair.
 *
 * Two deliberate divergences. The reference's rows and links are `<button onClick={nav}>`;
 * ours are `<Link>`s so middle-click, right-click and Cmd-click behave, which also means
 * the left-hand rows need `onMouseEnter` for the pane and a plain click for navigation.
 * And its bottom "Browse all categories" arrow is a hand-drawn animated SVG
 * (`.dnd-lm-arm`), which rule 0 forbids — this uses `<Icon name="arrow_forward" />`.
 */
export default function ProductsDropdown({ classification = [] }) {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);
  const {
    open,
    onMouseEnter: openMenu,
    close,
  } = useHoverIntent(undefined, () => setActiveId(null), NAV_MENU_GROUP);
  const rootRef = useRef(null);
  useDismiss(rootRef, open, close);

  const active =
    classification.find((category) => category.id === activeId) ||
    classification[0];
  const subcategories = active?.subcategories ?? [];

  return (
    <div ref={rootRef} className="relative z-[2]">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        className={`${NAV_ITEM_CLASS} cursor-pointer border-0 bg-transparent font-semibold text-blue-700`}
      >
        <Icon name="grid_view" size={18} weight={400} />
        All categories
        <Icon
          name="expand_more"
          size={18}
          weight={400}
          className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div className="cat-morph" data-open={open ? "true" : "false"}>
        <div className="cat-morph-menu">
          <div className="grid min-h-0 flex-1 grid-cols-[248px_minmax(0,1fr)] gap-[var(--space-4)]">
            {/* Left: every category */}
            <div className="dnd-thinscroll overflow-y-auto border-r border-line-subtle pr-[4px]">
              {classification.map((category) => {
                const isActive = category.id === active?.id;

                return (
                  <Link
                    key={`${category.id}-${category.name}`}
                    to={buildClassificationPath(category)}
                    onMouseEnter={() => setActiveId(category.id)}
                    onFocus={() => setActiveId(category.id)}
                    onClick={close}
                    className={`flex w-full cursor-pointer items-center gap-[10px] rounded-md px-[10px] py-[8px] text-left font-sans text-sm [transition:background_var(--duration-fast),color_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                      isActive
                        ? "bg-soft text-blue-700"
                        : "bg-transparent text-ink-2 hover:bg-soft hover:text-blue-700"
                    }`}
                  >
                    <Icon
                      name={categoryIcon(category.id)}
                      size={18}
                      weight={400}
                      className={`shrink-0 ${
                        isActive ? "text-blue-600" : "text-ink-3"
                      }`}
                    />
                    <span className="min-w-0 flex-1 truncate font-semibold">
                      {category.name}
                    </span>
                    <Icon
                      name="chevron_right"
                      size={16}
                      weight={400}
                      className="shrink-0 text-ink-3"
                    />
                  </Link>
                );
              })}
            </div>

            {/* Right: the active category's subcategories */}
            <div className="dnd-thinscroll min-w-0 overflow-y-auto py-[2px] pr-[4px]">
              <div className="px-[10px] py-[8px] text-2xs font-bold uppercase tracking-[var(--tracking-wide)] text-ink-3">
                {active?.name}
              </div>

              <div className="grid grid-cols-2 gap-[2px]">
                {subcategories.map((subcategory) => (
                  <Link
                    key={`${subcategory.id}-${subcategory.name}`}
                    to={buildClassificationPath(subcategory)}
                    onClick={close}
                    className="w-full cursor-pointer rounded-md bg-transparent px-[10px] py-[8px] text-left font-sans text-sm text-ink-2 [text-wrap:pretty] [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                  >
                    {subcategory.name}
                  </Link>
                ))}
              </div>

              {active && (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    navigate(buildClassificationPath(active));
                  }}
                  className="v2link mt-[8px] border-0 bg-transparent px-[10px] py-[6px] font-sans text-sm font-semibold text-blue-700 hover:text-blue-800 focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  Browse all {active.name}
                  <Icon
                    name="chevron_right"
                    size={16}
                    weight={400}
                    className="chev"
                  />
                </button>
              )}
            </div>
          </div>

          {/* The reference's `goShop` — the categories landing page, not the product
              listing. `/products` is still one click away inside it. */}
          <Link
            to="/categories"
            onClick={close}
            className="mt-[6px] flex w-full items-center justify-center gap-[var(--space-2)] border-0 border-t border-line-subtle bg-transparent px-[var(--space-3)] pb-[var(--space-2)] pt-[14px] font-sans text-sm font-semibold text-blue-700 hover:text-blue-800 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            Browse all categories
            <Icon name="arrow_forward" size={16} weight={400} />
          </Link>
        </div>
      </div>
    </div>
  );
}
